import CustomDropZone from '@/components/CustomDropZone';
import PageTemplate from '@/components/PageTemplate';
import Section from '@/components/Section';
import prepareTranslate from '@/helpers/dictionary';
import validations from '@/helpers/validation';
import {
  Button,
  Grid, Loader, MultiSelect, NumberInput, Stack, TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDebouncedCallback, useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import agentMutations from '@/api/mutitations/agent';
import { notifications } from '@mantine/notifications';
import EditFiles from '@/components/EditFiles';
import s3Mutations from '@/api/mutitations/s3';
import paymentProofMutations from '@/api/mutitations/paymentProof';
import Table from '@/components/Table';
import ViewPaymentProof from '@/components/ViewPaymentProof';
import Modal from '@/components/Modal/Index';
import { IconEdit, IconEye } from '@tabler/icons-react';
import { extractFilterOptions } from '@/components/Table/Table.functions';
import EditPaymentProof from '@/components/EditPaymentProof';

const PaymentConfirmation = () => {
  const t = prepareTranslate();
  const [loadingOpened, { open: openLoading, close: closeLoading }] = useDisclosure();
  const [viewOpened, { open: viewOpen, close: viewClose }] = useDisclosure(false);
  const [updateOpened, { open: updateOpen, close: updateClose }] = useDisclosure(false);

  const [submitLoadingOpened,
    { open: openSubmitLoading, close: closeSubmitLoading }] = useDisclosure();
  const { mutateAsync: createPaymentProofMutation } = paymentProofMutations.useCreatePaymentProof();
  const { mutateAsync: getAllPaymentProofMutation } = paymentProofMutations.useGetAll();
  const [key, setKey] = useState(0);
  const [modalLabel, setModalLabel] = useState('');
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [filterOptions, setFilterOptions] = useState({});

  const [data, setData] = useState([]);
  const { mutateAsync: agentMutate } = agentMutations.useGetAgents();
  const { mutateAsync: uploadMutate } = s3Mutations.useUpload();
  const [selectedPaymentProof, setselectedPaymentProof] = useState(null);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      files: [],
    },
    validate: {
      paymentProofNumber: (value) => validations.required(value),
      paymentDate: (value) => validations.required(value),
      associatedAgents: (value) => validations.required(value),
      value: (value) => {
        if (!value) return null;
        return validations.number(value);
      },
    },
  });

  const autoCompleteQuery = useDebouncedCallback(async (val) => {
    openLoading();

    if (val === '') {
      closeLoading();
      return;
    }

    const start = 1;
    const query = { 'personalDetails.fullName': val };

    try {
      const { data: agentData } = await agentMutate({ query, start });
      if (agentData.length !== 0) {
        const options = agentData.map((agent) => ({
          label: agent.personalDetails.fullName,
          value: agent._id,
        }));

        setData((prev) => {
          const merged = [...prev, ...options];
          return merged.filter(
            (opt, index, self) => (
              index === self.findIndex((o) => o.value === opt.value)),
          );
        });
      }
    } catch {
      notifications.show({
        title: t('pages.reports.notification.error.title'),
        message: t('pages.reports.notification.error.message'),
        color: 'red',
      });
    }

    closeLoading();
  }, 500);

  const handleAccept = (acceptedFiles) => {
    const formattedFiles = acceptedFiles.map((file) => ({ file, name: file?.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const handleAutocompleteChange = (val) => {
    const hasAgent = data.find((agent) => agent.label === val);

    if (!hasAgent) {
      autoCompleteQuery(val);
    }
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };

  const handleSubmit = async () => {
    const { files } = form.values;
    openSubmitLoading();
    if (files?.length > 0) {
      const formData = new FormData();

      files.forEach((file) => {
        const { file: fileData, name } = file;
        formData.append('files', fileData, name);
      });

      try {
        const uploadedFiles = await uploadMutate(formData);

        const clonedValue = {
          ...form.values,
          files: uploadedFiles.files.map((file) => ({
            path: file.path,
            name: file.name,
          })),
        };

        await createPaymentProofMutation(clonedValue);
        setModalLabel(t('components.viewAgentFiles.uploadSuccess'));
        openModal();
        form.reset();
        form.setValues({
          files: [], associatedAgents: [], paymentProofNumber: '', value: '', paymentDate: null, comments: '',
        });

        setKey((prev) => prev + 1);
      } catch (error) {
        setModalLabel(t('components.viewAgentFiles.uploadError'));
        openModal();
      } finally {
        closeSubmitLoading();
      }
    }
  };

  const handleClickOnView = (id) => {
    setselectedPaymentProof(id);
    viewOpen();
  };

  const handleClickOnEdit = (id) => {
    setselectedPaymentProof(id);
    updateOpen();
  };

  const mockColumns = [
    {
      title: t('pages.paymentConfirmation.table.titles.paymentProofNumber'),
      accessor: 'paymentProofNumber',
      visible: true,
      sortable: true,
      isFilterActive: true,
    },
    {
      title: t('pages.paymentConfirmation.table.titles.value'),
      accessor: 'value',
      visible: true,
      sortable: true,
      isFilterActive: true,
      filterType: 'select',
    },
    {
      title: t('pages.paymentConfirmation.table.titles.paymentDate'),
      accessor: 'paymentDate',
      visible: true,
      transforms: [{ func: 'date', params: { withTime: false } }],
      sortable: true,
      isFilterActive: true,
      filterType: 'datePicker',
    },
    {
      title: t('pages.paymentConfirmation.table.titles.associatedAgents'),
      accessor: 'associatedAgentsString',
      visible: true,
      isFilterActive: true,
      filterType: 'select',
    },
    {
      title: t('pages.paymentConfirmation.table.titles.createdAt'),
      accessor: 'createdAt',
      visible: true,
      transforms: [{ func: 'date', params: { withTime: false } }],
      sortable: true,
      isFilterActive: true,
      filterType: 'datePicker',
    },
    {
      accessor: 'actions',
      title: 'פעולות',
      visible: true,
      actions: [
        {
          title: 'צפייה',
          action: handleClickOnView,
          icon: <IconEye />,
          showLabel: true,
          isPrimary: true,
        },
        {
          title: 'עריכה',
          action: handleClickOnEdit,
          icon: <IconEdit />,
          showLabel: true,
          isPrimary: true,
        },
      ],
    },
  ];

  const initialColumnFilters = mockColumns
    .filter((column) => column.accessor !== 'actions')
    .reduce((acc, column) => {
      acc[column.accessor] = '';
      return acc;
    }, {});

  const [columnFilters, setColumnFilters] = useState(initialColumnFilters);

  const getData = async (start, end, query, sort) => {
    try {
      const result = await getAllPaymentProofMutation({
        start,
        end,
        query,
        sort,
      });

      const formattedResult = result.data.map((item) => {
        const agentsString = item.associatedAgents
          ?.map((agent) => agent.personalDetails?.fullName)
          .filter(Boolean)
          .join(', ');

        return {
          ...item,
          associatedAgentsString: agentsString || '',
        };
      });
      const newFilterOptions = extractFilterOptions(formattedResult, mockColumns);
      const allAgents = result.data
        .flatMap((item) => item.associatedAgents || [])
        .filter((agent) => agent.personalDetails?.fullName)
        .map((agent) => ({
          label: agent.personalDetails.fullName,
          value: agent._id,
        }));

      const uniqueAgentsMap = new Map();
      allAgents.forEach((agent) => {
        if (!uniqueAgentsMap.has(agent.value)) {
          uniqueAgentsMap.set(agent.value, agent);
        }
      });
      const uniqueAgents = Array.from(uniqueAgentsMap.values());

      newFilterOptions.associatedAgentsString = uniqueAgents;
      setFilterOptions(newFilterOptions);

      return { data: formattedResult, count: result.count };
    } catch (error) {
      notifications.show({
        title: t('pages.users.notification.errors.find.title'),
        message: t('pages.users.notification.errors.find.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
      return { data: [], count: 0 };
    }
  };

  return (
    <PageTemplate
      title={t('pages.paymentConfirmation.title')}
    >
      <Section>
        <Grid gutter="xl" mt={16}>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <TextInput
              required
              label={t('pages.paymentConfirmation.form.labels.paymentProofNumber')}
              {...form.getInputProps('paymentProofNumber')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <NumberInput
              hideControls
              label={t('pages.paymentConfirmation.form.labels.value')}
              {...form.getInputProps('value')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <DatePickerInput
              required
              maxDate={new Date()}
              label={t('pages.paymentConfirmation.form.labels.paymentDate')}
              {...form.getInputProps('paymentDate')}
            />
          </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
            <TextInput
              label={t('pages.paymentConfirmation.form.labels.comments')}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 12 }}>
            <MultiSelect
              required
              searchable
              rightSection={loadingOpened ? <Loader size="1rem" /> : null}
              onSearchChange={handleAutocompleteChange}
              data={data}
              label={t('pages.paymentConfirmation.form.labels.agents')}
              {...form.getInputProps('associatedAgents')}
            />
          </Grid.Col>
        </Grid >
      </Section>

      <Stack h='8rem' mb='2rem'>
        <CustomDropZone handleAccept={handleAccept} />
      </Stack>

      {form.values.files.length > 0 && (
        <>
          <EditFiles
            files={form.values.files}
            onNameChange={onFileNameChange}
            onRemove={onDeleteFile}
          />
          <Button
            loading={submitLoadingOpened}
            onClick={form.onSubmit(handleSubmit)}
          >{t('global.save')}</Button>
        </>
      )}
             <Table
               columns={mockColumns}
               getData={getData}
               isColumnsActive={false}
               isExportActive={false}
               key={key}
               columnFilters={columnFilters}
               setColumnFilters={setColumnFilters}
               filterOptions={filterOptions}
             />
     <ViewPaymentProof
        open={viewOpen}
        opened={viewOpened}
        close={viewClose}
        id={selectedPaymentProof}
      />
      <EditPaymentProof
      open={updateOpen}
      opened={updateOpened}
      close={updateClose}
      id={selectedPaymentProof}
      setKey={setKey}
      />

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        label={modalLabel}
        actions={
          [
           <Button key='closeButton' onClick={() => closeModal()}>{t('global.close')}</Button>,
          ]
        }
      />
    </PageTemplate>
  );
};

export default PaymentConfirmation;
